// Safety Armadillo Support Component
// Provides support ticket management interface

import { renderSafetyArmadillo } from '../utils/mascots.js';
import { escapeHtml } from '../utils/html-utils.js';

class SafetyArmadillo extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.currentView = 'list'; // 'list', 'detail', 'create'
    this.selectedTicketId = null;
    this.tickets = [];
    this.currentTicket = null;
    this.isLoading = false;
    this.ticketType = 'support'; // 'support', 'bug', 'feature'
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Remove existing listeners to prevent duplicates
    this.removeEventListeners();

    // Listen for open-support events from taco menu
    this.openSupportHandler = () => {
      this.open();
    };
    document.addEventListener('open-support', this.openSupportHandler);

    // Close on backdrop click
    this.backdropClickHandler = (e) => {
      if (e.target.classList.contains('safety-armadillo-backdrop')) {
        this.close();
      }
    };
    this.addEventListener('click', this.backdropClickHandler);

    // Handle button clicks
    this.buttonClickHandler = async (e) => {
      const closeBtn = e.target.closest('.close-btn');
      if (closeBtn) {
        this.close();
        return;
      }

      const createBtn = e.target.closest('[data-action="create-ticket"]');
      if (createBtn) {
        this.showCreateForm();
        return;
      }

      const ticketItem = e.target.closest('[data-ticket-id]');
      if (ticketItem) {
        const ticketId = ticketItem.dataset.ticketId;
        await this.viewTicket(ticketId);
        return;
      }

      const backBtn = e.target.closest('[data-action="back-to-list"]');
      if (backBtn) {
        this.showList();
        return;
      }

      const submitBtn = e.target.closest('[data-action="submit-ticket"]');
      if (submitBtn) {
        await this.submitTicket();
        return;
      }

      const submitCommentBtn = e.target.closest('[data-action="submit-comment"]');
      if (submitCommentBtn) {
        await this.submitComment();
        return;
      }

      const tabBtn = e.target.closest('[data-tab]');
      if (tabBtn) {
        this.ticketType = tabBtn.dataset.tab;
        this.updateCreateFormTabs();
        return;
      }
    };
    this.addEventListener('click', this.buttonClickHandler);

    // Handle form submission - these will be re-attached after render
    this.attachFormListeners();
  }

  removeEventListeners() {
    if (this.openSupportHandler) {
      document.removeEventListener('open-support', this.openSupportHandler);
    }
    if (this.backdropClickHandler) {
      this.removeEventListener('click', this.backdropClickHandler);
    }
    if (this.buttonClickHandler) {
      this.removeEventListener('click', this.buttonClickHandler);
    }
  }

  attachFormListeners() {
    // Handle form submission
    const createForm = this.querySelector('#createTicketForm');
    if (createForm && !createForm.dataset.listenerAttached) {
      createForm.dataset.listenerAttached = 'true';
      createForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this.submitTicket();
      });
    }

    const commentForm = this.querySelector('#commentForm');
    if (commentForm && !commentForm.dataset.listenerAttached) {
      commentForm.dataset.listenerAttached = 'true';
      commentForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this.submitComment();
      });
    }
  }

  open() {
    this.isOpen = true;
    this.currentView = 'list';
    this.render();
    this.loadTickets();
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  }

  close() {
    this.isOpen = false;
    this.currentView = 'list';
    this.selectedTicketId = null;
    this.currentTicket = null;
    this.render();
    document.body.style.overflow = ''; // Restore scrolling
  }

  showList() {
    this.currentView = 'list';
    this.selectedTicketId = null;
    this.currentTicket = null;
    this.render();
    this.loadTickets();
  }

  showCreateForm() {
    this.currentView = 'create';
    this.ticketType = 'support';
    this.render();
  }

  async viewTicket(ticketId) {
    this.selectedTicketId = ticketId;
    this.currentView = 'detail';
    this.render();
    await this.loadTicket(ticketId);
  }

  async loadTickets() {
    this.isLoading = true;
    this.updateLoadingState();

    try {
      const response = await window.API.request('/support/tickets?status=all&limit=50');
      this.tickets = response.tickets || [];
      this.renderTicketList();
    } catch (error) {
      window.logger.error('Failed to load tickets:', error);
      const message = error?.statusCode === 503
        ? 'The help desk is starting up. Please try again in a minute.'
        : 'Failed to load tickets. Please try again.';
      this.showError(message);
    } finally {
      this.isLoading = false;
      this.updateLoadingState();
    }
  }

  async loadTicket(ticketId) {
    this.isLoading = true;
    this.updateLoadingState();

    try {
      const ticket = await window.API.request(`/support/tickets/${ticketId}`);
      this.currentTicket = ticket;
      this.renderTicketDetail();
    } catch (error) {
      window.logger.error('Failed to load ticket:', error);
      this.showError('Failed to load ticket. Please try again.');
    } finally {
      this.isLoading = false;
      this.updateLoadingState();
    }
  }

  async submitTicket() {
    const form = this.querySelector('#createTicketForm');
    if (!form) return;

    const formData = new FormData(form);
    const subject = formData.get('subject')?.trim();
    const body = formData.get('body')?.trim();

    if (!subject || !body) {
      this.showError('Please fill in all required fields.');
      return;
    }

    const submitBtn = this.querySelector('[data-action="submit-ticket"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.querySelector('.btn-text').textContent = 'Creating...';
    }

    try {
      await window.API.request('/support/tickets', {
        method: 'POST',
        body: JSON.stringify({
          subject,
          body,
          type: this.ticketType,
          priority: 'normal'
        })
      });

      // Show success message
      this.showMessage('Ticket created successfully!', 'success');

      // Reset form and go back to list
      form.reset();
      setTimeout(() => {
        this.showList();
        this.loadTickets();
      }, 1000);
    } catch (error) {
      window.logger.error('Failed to create ticket:', error);
      this.showError(error.message || 'Failed to create ticket. Please try again.');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.querySelector('.btn-text').textContent = 'Create Ticket';
      }
    }
  }

  async submitComment() {
    const form = this.querySelector('#commentForm');
    if (!form) return;

    const formData = new FormData(form);
    const body = formData.get('body')?.trim();

    if (!body) {
      this.showError('Please enter a comment.');
      return;
    }

    const submitBtn = this.querySelector('[data-action="submit-comment"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.querySelector('.btn-text').textContent = 'Sending...';
    }

    try {
      await window.API.request(`/support/tickets/${this.selectedTicketId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body })
      });

      // Reload ticket to show new comment
      form.reset();
      await this.loadTicket(this.selectedTicketId);
      this.showMessage('Comment added successfully!', 'success');
    } catch (error) {
      window.logger.error('Failed to add comment:', error);
      this.showError(error.message || 'Failed to add comment. Please try again.');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.querySelector('.btn-text').textContent = 'Add Comment';
      }
    }
  }

  updateLoadingState() {
    const loadingEl = this.querySelector('.loading-overlay');
    if (loadingEl) {
      loadingEl.style.display = this.isLoading ? 'flex' : 'none';
    }
  }

  updateCreateFormTabs() {
    const tabs = this.querySelectorAll('[data-tab]');
    tabs.forEach(tab => {
      tab.classList.toggle('active', tab.dataset.tab === this.ticketType);
    });
  }

  showError(message) {
    this.showMessage(message, 'error');
  }

  showMessage(message, type = 'info') {
    // Remove existing messages
    const existing = this.querySelector('.message-banner');
    if (existing) existing.remove();

    const banner = document.createElement('div');
    banner.className = `message-banner message-${type}`;
    banner.textContent = message;
    banner.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      background: ${type === 'error' ? '#dc3545' : '#28a745'};
      color: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      z-index: 10001;
      animation: slideIn 0.3s ease-out;
    `;

    document.body.appendChild(banner);
    setTimeout(() => banner.remove(), 5000);
  }

  formatDate(dateString) {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  getStatusBadge(status) {
    const statusMap = {
      'open': { text: 'Open', color: '#28a745' },
      'closed': { text: 'Closed', color: '#6c757d' },
      'pending': { text: 'Pending', color: '#ffc107' },
      'active': { text: 'Active', color: '#17a2b8' }
    };
    const statusInfo = statusMap[status?.toLowerCase()] || { text: status || 'Unknown', color: '#6c757d' };
    return `<span class="status-badge" style="background: ${statusInfo.color}; color: white; padding: 0.25rem 0.5rem; border-radius: 4px; font-size: 0.75rem; font-weight: 600;">${statusInfo.text}</span>`;
  }

  renderTicketList() {
    const listContainer = this.querySelector('.tickets-list');
    if (!listContainer) return;

    if (this.tickets.length === 0) {
      listContainer.innerHTML = `
        <div class="empty-state">
          <div class="empty-icon">${renderSafetyArmadillo()}</div>
          <p>No tickets yet. Create your first support request!</p>
        </div>
      `;
      return;
    }

    listContainer.innerHTML = this.tickets.map(ticket => `
      <div class="ticket-item" data-ticket-id="${ticket.id || ticket.conversation_id || ticket.number}">
        <div class="ticket-header">
          <h4 class="ticket-subject">${escapeHtml(ticket.subject || ticket.title || 'No Subject')}</h4>
          ${this.getStatusBadge(ticket.status)}
        </div>
        <div class="ticket-meta">
          <span class="ticket-type">${ticket.type || 'support'}</span>
          <span class="ticket-date">${this.formatDate(ticket.created_at || ticket.date)}</span>
        </div>
        ${ticket.body || ticket.preview ? `<p class="ticket-preview">${escapeHtml((ticket.body || ticket.preview || '').substring(0, 150))}${(ticket.body || ticket.preview || '').length > 150 ? '...' : ''}</p>` : ''}
      </div>
    `).join('');
  }

  renderTicketDetail() {
    const detailContainer = this.querySelector('.ticket-detail');
    if (!detailContainer || !this.currentTicket) return;

    let thread = this.currentTicket.thread || this.currentTicket.conversation || [];

    // Sort thread by created_at ascending (oldest first)
    thread = [...thread].sort((a, b) => {
      const dateA = new Date(a.created_at || a.date || 0);
      const dateB = new Date(b.created_at || b.date || 0);
      return dateA - dateB; // Ascending order (oldest first)
    });

    detailContainer.innerHTML = `
      <div class="ticket-detail-header">
        <div>
          <h3>${escapeHtml(this.currentTicket.subject || this.currentTicket.title || 'No Subject')}</h3>
          <div class="ticket-meta">
            ${this.getStatusBadge(this.currentTicket.status)}
            <span>Created: ${this.formatDate(this.currentTicket.created_at || this.currentTicket.date)}</span>
          </div>
        </div>
      </div>

      <div class="ticket-thread">
        ${thread.map((message) => {
    // Determine if message is from customer or agent/staff
    const isCustomer = message.type === 'customer' || message.from === 'customer';
    // Get sender name - use from_user from backend if available (agent name), otherwise default
    const senderName = isCustomer
      ? 'You'
      : (message.from_user || 'Support Team');

    return `
          <div class="thread-message ${isCustomer ? 'customer' : 'agent'}">
            <div class="message-header">
              <strong>${escapeHtml(senderName)}</strong>
              <span class="message-date">${this.formatDate(message.created_at || message.date)}</span>
            </div>
            <div class="message-body">${this.formatMessageBody(message.body || message.text || '')}</div>
          </div>
        `;
  }).join('')}
      </div>

      <form id="commentForm" class="comment-form">
        <div class="form-group">
          <label for="commentBody">Add a comment</label>
          <textarea 
            id="commentBody" 
            name="body" 
            rows="4" 
            placeholder="Type your reply here..."
            required
          ></textarea>
        </div>
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" data-action="back-to-list">Back to List</button>
          <button type="submit" class="btn btn-primary" data-action="submit-comment">
            <span class="btn-text">Add Comment</span>
          </button>
        </div>
      </form>
    `;
  }

  formatMessageBody(body) {
    if (!body) return '';

    // Check if body contains HTML tags
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(body);

    if (hasHtmlTags) {
      // Body contains HTML - sanitize and render it safely using DOMParser
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(body, 'text/html');

        // Remove dangerous elements
        const dangerous = doc.querySelectorAll('script, style, iframe, object, embed, form, input, button');
        dangerous.forEach(el => el.remove());

        // Remove dangerous attributes from all elements
        const allElements = doc.querySelectorAll('*');
        allElements.forEach(el => {
          // Remove all attributes that could be dangerous
          Array.from(el.attributes).forEach(attr => {
            const attrName = attr.name.toLowerCase();
            if (attrName.startsWith('on') || // event handlers
                attrName === 'href' && attr.value.toLowerCase().startsWith('javascript:') ||
                attrName === 'src' && attr.value.toLowerCase().startsWith('javascript:')) {
              el.removeAttribute(attr.name);
            }
          });
        });

        // Get sanitized HTML from body
        const sanitized = doc.body.innerHTML;
        return sanitized;
      } catch (error) {
        // If parsing fails, fall back to escaping
        window.logger?.warn('Failed to parse HTML, falling back to escaped text', error);
        return escapeHtml(body).replace(/\n/g, '<br>');
      }
    } else {
      // Plain text - escape and add basic formatting
      return escapeHtml(body)
        .replace(/\n/g, '<br>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');
    }
  }


  render() {
    if (!this.isOpen) {
      this.innerHTML = '';
      return;
    }

    this.innerHTML = `
      <div class="safety-armadillo-backdrop">
        <div class="safety-armadillo-modal">
          <div class="modal-header">
            <div class="header-content">
              <div class="armadillo-icon">${renderSafetyArmadillo()}</div>
              <h2>Safety Armadillo Support</h2>
            </div>
            <button class="close-btn" aria-label="Close">×</button>
          </div>

          <div class="modal-body">
            ${this.renderCurrentView()}
          </div>

          <div class="loading-overlay" style="display: none;">
            <div class="spinner"></div>
          </div>
        </div>
      </div>
    `;

    // Re-attach form listeners after render
    this.attachFormListeners();
  }

  renderCurrentView() {
    switch (this.currentView) {
    case 'create':
      return this.renderCreateForm();
    case 'detail':
      return this.renderDetailView();
    default:
      return this.renderListView();
    }
  }

  renderListView() {
    return `
      <div class="view-header">
        <h3>Your Support Tickets</h3>
        <button class="btn btn-primary" data-action="create-ticket">
          <span class="btn-text">+ New Ticket</span>
        </button>
      </div>
      <div class="tickets-list">
        ${this.isLoading ? '<div class="loading">Loading tickets...</div>' : ''}
      </div>
    `;
  }

  renderCreateForm() {
    return `
      <div class="view-header">
        <h3>Create Support Ticket</h3>
        <button class="btn btn-secondary" data-action="back-to-list">Back</button>
      </div>

      <div class="ticket-type-tabs">
        <button class="tab ${this.ticketType === 'support' ? 'active' : ''}" data-tab="support">
          💬 Support Request
        </button>
        <button class="tab ${this.ticketType === 'bug' ? 'active' : ''}" data-tab="bug">
          🐛 Bug Report
        </button>
        <button class="tab ${this.ticketType === 'feature' ? 'active' : ''}" data-tab="feature">
          ✨ Feature Request
        </button>
      </div>

      <form id="createTicketForm" class="create-ticket-form">
        <div class="form-group">
          <label for="ticketSubject">Subject *</label>
          <input 
            type="text" 
            id="ticketSubject" 
            name="subject" 
            placeholder="Brief description of your issue or request"
            maxlength="200"
            required
          />
        </div>
        <div class="form-group">
          <label for="ticketBody">Description *</label>
          <textarea 
            id="ticketBody" 
            name="body" 
            rows="8" 
            placeholder="Please provide as much detail as possible..."
            maxlength="10000"
            required
          ></textarea>
        </div>
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" data-action="back-to-list">Cancel</button>
          <button type="submit" class="btn btn-primary" data-action="submit-ticket">
            <span class="btn-text">Create Ticket</span>
          </button>
        </div>
      </form>
    `;
  }

  renderDetailView() {
    return `
      <div class="ticket-detail">
        ${this.isLoading ? '<div class="loading">Loading ticket...</div>' : ''}
      </div>
    `;
  }
}

// Register the custom element
customElements.define('safety-armadillo', SafetyArmadillo);

